import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  { 
    id: "graph1", 
    title: "Análisis general", 
    menuTitle: "General", 
    text: `Entre marzo de 2023 y noviembre de 2025, la cantidad de niñas y niños en cuidados alternativos en Chile aumentó un 22%. Este incremento se 
          tradujo principalmente en un 28% más de ingresos a familias de acogida (FAE), alcanzando un total de 10.455 casos. Por su parte, la cantidad 
          de niñas y niños en residencias creció en un 12%, cerrando el periodo con 5.139 ingresos. Esto muestra que, aunque las familias de acogida se 
          consolidan como la medida de cuidado prioritaria y de mayor crecimiento, el total de ingresos a residencias continúa al alza, reflejando una 
          demanda creciente por cuidados alternativos en el país.`, 
    link: "https://public.flourish.studio/visualisation/24942326/embed"
  },
  { 
    id: "graph2", 
    title: "Análisis por sexo", 
    menuTitle: "\u2022 Sexo", 
    text: `Al analizar la distribución por sexo, se observa un aumento sostenido en ambas modalidades de cuidado, aunque con ritmos diferenciados. En 
          las Familias de Acogida, la cantidad de niños creció un 30%, superando el incremento del 26% registrado en las niñas. Por su parte, en la 
          modalidad de Residencia, la brecha de crecimiento es aún más marcada: la población de niñas aumentó un 15%, frente a un alza del 8% en los 
          niños durante el mismo periodo.`, 
    link: "https://public.flourish.studio/visualisation/24942530/embed"
  },
  { 
    id: "graph3", 
    title: "Análisis por edad", 
    menuTitle: "\u2022 Edad", 
    text: `La distribución por edad revela dinámicas distintas según la modalidad de cuidado. En las Familias de Acogida, aunque el grupo predominante 
          es el de 4 a 11 años con 4.652 casos, los crecimientos más acelerados se observan en los extremos: el grupo de 18 años y más se duplicó, pasando 
          de 49 a 101 casos (+106%), seguido por los tramos de 0 a 3 años (+36%) y de 12 a 17 años (+30%).

En las residencias, el grupo más numeroso sigue siendo el de 12 a 17 años con 2.599 casos. El mayor crecimiento porcentual en esta modalidad también se 
      registró en uno de los tramos extremos: los jóvenes de 18 años y más aumentaron un 41% (pasando de 397 a 558 casos). Esta tendencia evidencia la 
      dificultad del sistema residencial para lograr el egreso de adolescentes antes de que alcancen la mayoría de edad, resultando en una permanencia 
      que se extiende hasta la adultez temprana.

Un punto crítico se observa en la primera infancia. A pesar de que la ley mandata priorizar el entorno familiar para niños de 0 a 3 años [11], los datos no 
      muestran un avance hacia la desinternación de este grupo: la cantidad de lactantes en residencias aumentó de 547 a 689 niños (+26%) entre abril 
      de 2023 y noviembre de 2025. De hecho, al analizar al total de niñas y niños de entre 0 y 3 años que se encuentran en cuidado alternativo (2.994 casos), 
      casi 1 de cada 4 (23%) sigue siendo derivado a una residencia en lugar de a una familia de acogida.

Nota: *Se utiliza abril de 2023 como base comparativa debido a que los datos previos fueron publicados bajo una estructura distinta por el servicio.*`, 
    link: "https://public.flourish.studio/visualisation/24942709/embed"
  },
  { 
    id: "graph4", 
    title: "Análisis por región", 
    menuTitle: "\u2022 Región", 
    text: `Se observa una tendencia general de crecimiento en las Familias de Acogida en la mayoría de las regiones. Destacan casos como la Región de Aysén, 
          que prácticamente duplicó su alcance con un alza del 90% (pasando de 21 a 40 casos), y Arica y Parinacota, donde el acogimiento familiar creció un 
          88% (de 58 a 109 casos). En la Región del Maule, el incremento también fue masivo, alcanzando un 74% (de 488 a 851 casos).

En contraste, la modalidad de Residencia muestra un comportamiento dispar. Experimentó aumentos significativos en Tarapacá, con un aumento del 69% (pasando 
      de 140 a 237 casos); Arica y Parinacota, con 50% de crecimiento (de 109 a 164 casos); y Atacama, que creció un 51% (de 81 a 122 casos). Por otro lado, 
      algunas regiones lograron reducir su población institucional: Los Lagos registró la baja más importante con un 20% (pasando de 284 a 228), seguida por 
      Biobío con una disminución del 6,5% (de 494 a 462 casos).

En resumen, los datos demuestran una expansión consolidada de los programas FAE, mientras que la situación de las residencias varía drásticamente: en el norte 
      del país la presión institucional sigue en aumento, mientras que en zonas del centro y sur se observa un avance hacia la desinternación.`, 
    link: "https://public.flourish.studio/visualisation/24942827/embed"
  }
];

const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
              key={graph.id}
              onClick={() => handleMenuItemClick(graph.id)}
              style={{ color: 'black', paddingLeft: graph.id !== "graph1" ? "30px" : "20px" }}
            >
              {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}> 
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "660px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;

